import React, { useEffect, useState } from "react";
import TopBar from "@/components/TopBar";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Copy, Users, Gift, Twitter, MessageCircle } from "lucide-react";

export default function Referrals() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/referrals/me").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const link = data ? `${window.location.origin}/register?ref=${data.referral_code}` : "";
  const copy = () => { navigator.clipboard.writeText(link); toast.success(t("link_copied")); };
  const tweet = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(t("referral_tweet_text"))}&url=${encodeURIComponent(link)}`, "_blank");
  const telegram = () => window.open(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(t("referral_share"))}`, "_blank");

  return (
    <div className="min-h-screen bg-app text-white">
      <TopBar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6">{t("referrals")}</h1>

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-px bg-line mb-6">
          <Stat icon={Users} label={t("total_invites")} value={data?.counts?.total ?? 0} testid="stat-total" />
          <Stat icon={Gift} label={t("awarded")} value={data?.counts?.awarded ?? 0} testid="stat-awarded" />
          <Stat icon={Gift} label={t("total_earned")} value={`$${(data?.total_earned ?? 0).toFixed(2)}`} testid="stat-earned" />
        </div>

        {/* Referral link */}
        <div className="bg-panel border border-line rounded-sm p-5 mb-6">
          <div className="text-[10px] uppercase tracking-widest text-dim mb-2">{t("your_referral_link")}</div>
          <div className="flex flex-col sm:flex-row items-stretch gap-2">
            <div className="flex-1 bg-app border border-line px-3 py-2.5 rounded-sm font-mono text-sm break-all" data-testid="referral-link">{link || "…"}</div>
            <button data-testid="referral-copy" onClick={copy} className="px-4 py-2.5 border border-line hover:bg-panel-hover rounded-sm flex items-center justify-center gap-2 text-xs uppercase tracking-widest"><Copy className="w-4 h-4" />{t("copy")}</button>
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <button data-testid="referral-tweet" onClick={tweet} className="flex-1 py-2.5 border border-line hover:bg-panel-hover rounded-sm flex items-center justify-center gap-2 text-xs uppercase tracking-widest"><Twitter className="w-4 h-4" />{t("share_on_x")}</button>
            <button data-testid="referral-telegram" onClick={telegram} className="flex-1 py-2.5 border border-line hover:bg-panel-hover rounded-sm flex items-center justify-center gap-2 text-xs uppercase tracking-widest"><MessageCircle className="w-4 h-4" />{t("telegram")}</button>
          </div>
          <div className="mt-4 text-xs text-muted">
            {t("earn_per_referral_prefix")} <span className="text-buy font-mono">${(data?.bonus_per_referral ?? 10).toFixed(2)}</span>{t("earn_per_referral_suffix")}
          </div>
        </div>

        {/* Referrals table */}
        <div className="bg-panel border border-line rounded-sm">
          <div className="border-b border-line px-4 py-3 text-xs uppercase tracking-widest font-semibold">{t("your_referrals")}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono min-w-[500px]">
              <thead className="bg-app border-b border-line">
                <tr className="text-dim uppercase tracking-widest text-[10px]">
                  <th className="text-left px-4 py-2">{t("date")}</th>
                  <th className="text-left px-2 py-2">{t("email")}</th>
                  <th className="text-right px-2 py-2">{t("bonus")}</th>
                  <th className="text-right px-4 py-2">{t("status")}</th>
                </tr>
              </thead>
              <tbody>
                {(!data?.referrals || data.referrals.length === 0) && <tr><td colSpan={4} className="text-center py-8 text-dim">{t("no_referrals")}</td></tr>}
                {(data?.referrals || []).map((r) => (
                  <tr key={r.id} data-testid={`ref-row-${r.id}`} className="border-b border-subtle hover:bg-panel-hover">
                    <td className="px-4 py-2 text-muted whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td className="px-2 py-2">{r.email}</td>
                    <td className="px-2 py-2 text-right">${Number(r.amount || 0).toFixed(2)}</td>
                    <td className={`px-4 py-2 text-right uppercase ${r.status === "awarded" ? "text-buy" : "text-orange-400"}`}>{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-panel p-4">
    <div className="flex items-center gap-2 mb-2"><Icon className="w-4 h-4 text-buy" /><div className="text-[10px] uppercase tracking-widest text-dim">{label}</div></div>
    <div className="font-mono text-xl sm:text-2xl font-bold" data-testid={testid}>{value}</div>
  </div>
);
